import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { buildFamilies } from '@evi/store-kit';
import { useCatalog } from '@/hooks/useCatalog';
import { FamilyCard } from '@/components/FamilyCard';
import { TalkToSales } from '@/components/TalkToSales';
import { FilmHero } from '@/components/FilmHero';
import { InstallerBand } from '@/components/InstallerBand';
import styles from './CommercialDcPage.module.css';

/** Commercial DC sales page (/commercial-dc): cinematic hero, the DC families
 *  for online purchase (variant select + card checkout on the detail page), the
 *  inclusive-commissioning line, and Talk to sales for site projects / freight
 *  quotes. */
export function CommercialDcPage() {
  const { data: catalog, isLoading, isError } = useCatalog();
  const families = catalog ? buildFamilies(catalog).filter((f) => f.tier === 'dc') : [];

  // Deep-link target: links to /commercial-dc#talk-to-sales (e.g. the cart's
  // large-order "Talk to sales" affordance) scroll straight to the sales form.
  // The shop has no global hash-scroll, so the page handles its own anchor.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#talk-to-sales') return;
    const el = document.getElementById('talk-to-sales');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  return (
    <div className={styles.page}>
      <FilmHero video="/media/products/dcfc/hero-loop.mp4" poster="/media/products/dcfc/hero-loop-poster.jpg"
        framing={50} label="Commercial DC fast charging">
        <h1 className={styles.heroTitle}>Commercial DC fast charging</h1>
        <p className={styles.heroSub}>
          40 kW to 240 kW. Buy online and ship to site. Each charger goes live after it
          connects, passes its plug test, and EV Initiative approves the launch. Every DC charger
          is priced as one inclusive number that <strong>includes pre-commissioning to the
          network</strong>.
        </p>
        {/* Was a bare text link; promoted to a full button (Warren) so
            it is findable at a glance. */}
        <Link to="/incentives" className={`eviBtn eviBtnPrimary ${styles.incentivesLink}`}>
          Check incentives in your area &rarr;
        </Link>
      </FilmHero>

      <section className={styles.section}>
        <h2 className={styles.h2}>Shop DC chargers</h2>
        {isLoading && <p className={styles.state}>Loading the catalog…</p>}
        {isError && <p className={styles.state}>Could not load the catalog.</p>}
        <div className={styles.grid}>
          {families.map((f) => (
            <FamilyCard key={f.key} family={f} />
          ))}
        </div>
      </section>

      {/* Installers are the main buyers of DC, so the installer band also sits
          between the grid and Talk to sales (Warren, 8 Oct 2026). */}
      <InstallerBand className={styles.installerBand} />

      <section className={styles.sales} id="talk-to-sales">
        <TalkToSales />
      </section>
    </div>
  );
}
